/**
 * Runs a state update inside a View Transition, where the browser has one.
 *
 * The browser snapshots the page before and after the update and cross-fades
 * between them. No library, no animation code, and nothing to keep in sync with
 * the markup — the whole point is that it works on a change we describe purely
 * as state.
 *
 * Three ways it declines, all of them fine:
 *
 * - **No support.** Older Safari and Firefox have no `startViewTransition`, so
 *   the update just happens. Nothing is animated and nothing is broken.
 * - **Reduced motion.** Checked here rather than left to CSS: the API's default
 *   animation is not ours to override reliably, and a user who asked for less
 *   movement should get none rather than a shorter version of it.
 * - **A transition already running.** Starting a second one mid-flight makes the
 *   browser skip the first, which looks like a stutter. Tabs are easy to
 *   double-tap, so this matters.
 *
 * `flushSync` is required, not incidental: React 18 batches updates, and the
 * callback has to leave the DOM in its *after* state before it returns or the
 * browser snapshots the old page twice and cross-fades it with itself.
 */

import { flushSync } from "react-dom";

interface ViewTransition {
  finished: Promise<void>;
}

type WithViewTransitions = Document & {
  startViewTransition?: (callback: () => void) => ViewTransition;
};

let running = false;

export function withViewTransition(update: () => void): void {
  const start = (document as WithViewTransitions).startViewTransition;
  const reduced =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (!start || reduced || running) {
    update();
    return;
  }

  running = true;
  const transition = start.call(document, () => flushSync(update));
  transition.finished.finally(() => {
    running = false;
  });
}
